import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Clock, ChevronDown, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "AI Governance",
  "PMO Transformation",
  "Cybersecurity",
  "Multiple areas",
  "Not sure yet",
];

const inputClass =
  "w-full rounded-none border-0 border-b border-white/20 bg-transparent px-0 py-3 text-sm text-white placeholder:text-slate-500 shadow-none focus-visible:border-accent focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors sm:text-base";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: INTERESTS[0], message: "" });
  const [honeypot, setHoneypot] = useState("");
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, website: honeypot });
      toast.success("Request received — we'll reply within one business day.", {
        description: "Your enquiry has gone straight to the AiCendor team.",
      });
      setForm({ name: "", email: "", company: "", interest: INTERESTS[0], message: "" });
    } catch (err) {
      toast.error("Something went wrong — please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>Prefer to write first?</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-8 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
                Make control your
                <br />
                <span className="text-accent">competitive edge.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
                Not ready to pick a time? Tell us where AI, delivery or security pressure is building. We&rsquo;ll come back
                with a view on how we&rsquo;d approach it — honestly, and without obligation.
              </p>
            </Reveal>
            <div className="mt-12 space-y-5">
              <Reveal delay={0.25}>
                <a
                  href="mailto:info@aicendor.com"
                  data-testid="contact-email-link"
                  className="group flex items-center gap-4 text-sm text-slate-300 transition-colors hover:text-white sm:text-base"
                >
                  <span className="flex h-10 w-10 items-center justify-center border border-white/15 transition-colors group-hover:border-gold">
                    <Mail className="h-4 w-4 text-gold" />
                  </span>
                  info@aicendor.com
                </a>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="flex items-center gap-4 text-sm text-slate-400 sm:text-base">
                  <span className="flex h-10 w-10 items-center justify-center border border-white/15">
                    <Clock className="h-4 w-4 text-accent" />
                  </span>
                  Replies within one business day
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-10 lg:pt-10">
              <div className="grid gap-10 sm:grid-cols-2 sm:gap-8">
                <div>
                  <label htmlFor="contact-name" className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    Full name *
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    required
                    maxLength={120}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Alex Morgan"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    Work email *
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    maxLength={160}
                    value={form.email}
                    onChange={set("email")}
                    placeholder="alex@company.com"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid gap-10 sm:grid-cols-2 sm:gap-8">
                <div>
                  <label htmlFor="contact-company" className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    Company
                  </label>
                  <input
                    id="contact-company"
                    data-testid="contact-company-input"
                    type="text"
                    maxLength={160}
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Company Ltd."
                    className={inputClass}
                  />
                </div>
                <div className="relative">
                  <label htmlFor="contact-interest" className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    I&rsquo;m interested in
                  </label>
                  <select
                    id="contact-interest"
                    data-testid="contact-interest-select"
                    value={form.interest}
                    onChange={set("interest")}
                    className={`${inputClass} appearance-none pr-8`}
                  >
                    {INTERESTS.map((i) => (
                      <option key={i} value={i} className="bg-surface text-white">
                        {i}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className="pointer-events-none absolute bottom-4 right-1 h-4 w-4 text-slate-500"
                    aria-hidden="true"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-message" className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                  What are you trying to get under control? *
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  maxLength={4000}
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="A few lines on your AI plans, delivery challenges or security posture…"
                  className={`${inputClass} resize-none`}
                />
              </div>

              {/* Honeypot — hidden from humans */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="hidden"
              />

              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="btn-sweep btn-sweep-light flex items-center gap-3 border border-accent bg-accent px-10 py-4 font-mono text-xs font-medium uppercase tracking-[0.22em] text-ink"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      Send enquiry <ArrowUpRight className="h-4 w-4" />
                    </>
                  )}
                </button>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  Confidential. No obligation.
                </span>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
